/* eslint-disable complexity */
import { Component, DoCheck, inject, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { fromSelectors } from '@store/selectors';
import { fromActions } from '@store/actions';
import { ConnectionService } from '@services/connection-service';
import { EventLoggerService } from '@services/event-logger-service';
import { BroadcastService } from '@services/broadcast-service';
import { Store } from '@ngrx/store';
import { Location } from '@angular/common';
import { AnimationOptions } from 'ngx-lottie';
import { LocalStorageService } from '@services/local-storage-service';
import { WindowRefService } from '@services/window-ref-service';
import { CacheService } from '@services/cache-service';
import { CartService } from '@services/cart-service/cart.service';
import { CartUpdateOptions } from '@shared/types/cart.types';
import { AppConfig } from '../../app.config';
import { ExperimentService } from '@services/experiment/experiment.service';

const shopSectionNames = {
  collection: 'collection',
  reorderProducts: 'reorderProducts',
  recommendedProducts: 'recommendedProducts',
  bodyProducts: 'bodyProducts',
  scalpProducts: 'scalpProducts',
  lipProducts: 'lipProducts',
}
@Component({
  selector: 'user-order-reorder',
  templateUrl: './user-order-reorder.html',
  standalone: false,
})

export class UserOrderReorderComponent implements OnDestroy, DoCheck {
  subscriptions: Array<Subscription> = [];
  productsAvailable: Array<any> = [];
  totalSP: number = 0;
  totalMRP: number = 0;
  discount: number = 0;
  nonRxLimit: number = 5;
  rxLimit: number = 3;
  maxPrice: number;
  cartValue: number;
  allShopProducts: any = [];
  isBOGOSaleLive: boolean = false;
  isSearchOn: boolean = false;
  isAdvancedSearchOn: boolean = false;
  allSaleProducts: any = [];
  searchProducts: any = [];
  percentageDone: number;
  saleVariants: any;
  searchKey: string;
  amountRemaining: any;
  aovTask: boolean = false;
  prodImage: any;
  loading: boolean = true;
  barStyle: any = {};
  tipStyle: any = {};
  freeProduct: any;
  cart: any;
  cartItems: any = [];
  user: any;
  options: AnimationOptions = {
    path: '/assets/animation.json',
  };
  styles: Partial<CSSStyleDeclaration> = {
    marginTop: '-200px',
    position: 'sticky',
    bottom: '0',
  };
  animationProduct: any;
  showConfettiAnimation: boolean = false;
  aovData: any;
  scrollIdData: any;
  oldProduct: any;
  newProduct: any;
  selfReorderWarning: boolean;
  isDiscontinued: boolean = false;
  // productsInCart - map of products with id and quantity. ex: { 'xd03q56': 2 }
  productsInCart: { [key: string]: number } = {};
  toolbarTitle: string = 'Select Products';
  isReplaced: boolean = false;
  toolbarIcons: Array<any> = [{
    clickId: 'cart_page',
    name: this.appConfig.Toolbar.ICONS.CART,
    callback: (): void => this.goToCart(),
    cartValue: null,
  }];
  experiments: Array<any> = [];
  isPersonalizedBogoExperiment: boolean = false;
  personalizedBogoTitle: any;
  refactoredSections: Array<string> = ['reorderProducts', 'recommendedProducts', 'bodyProducts', 'scalpProducts', 'lipProducts'];
  tagCacheTableMapping: Record<string, string> = AppConfig.TagCacheTableMapping;
  newCartUI: boolean = false;
  minimumCash: any;
  cashBalance: any;
  tempCashBalance: any;
  showCashBalance: boolean = false;
  isShopRevampExperimentOn: boolean = false;
  collectionTitle: string = '';


  private readonly conn: ConnectionService = inject(ConnectionService);
  private readonly router: Router = inject(Router);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly eventLogger: EventLoggerService = inject(EventLoggerService);
  private readonly broadcast: BroadcastService = inject(BroadcastService);
  private readonly location: Location = inject(Location);
  private readonly windowRef: WindowRefService = inject(WindowRefService);
  private readonly localStorage: LocalStorageService = inject(LocalStorageService);
  private readonly cacheService: CacheService = inject(CacheService);
  private readonly cartService: CartService = inject(CartService);
  private readonly store: Store = inject(Store);
  private readonly experimentService: ExperimentService = inject(ExperimentService);

  constructor(
    protected appConfig: AppConfig,
  ) {
    this.subscriptions.push(
      this.broadcast.on('REFRESH_SHOP_CART').subscribe((payload: { productsInCart?: { [key: string]: number } } | undefined) => {
        this.getCart();
        this.store.dispatch(fromActions.CartUpdateProductsBegin({ products: payload?.productsInCart || {} }));
        this.calculateDiscount();
      })
    );
  }

  ngDoCheck(): void {
    const id = this.localStorage.getValue('scrollID');
    const windowRef = this.windowRef.nativeWindow;
    const doc = windowRef.document;
    const lifestyle = doc.getElementById(id);
    if (!this.loading && lifestyle) {
      lifestyle.scrollIntoView({ behavior: 'smooth', block: 'end' });
      this.localStorage.delete('scrollID');
    }
    if (this.newProduct?.objectId && this.isReplaced) {
      const replacedProductID = doc.getElementById(this.newProduct.objectId);
      if (!this.loading && replacedProductID) {
        replacedProductID.scrollIntoView({ block: 'end', behavior: 'smooth' });
        this.isReplaced = false;
      }
    }
  }

  async ngOnInit(): Promise<any> {
    this.subscriptions = [];
    this.user = this.conn.getActingUser();
    this.toolbarIcons[0].showCartUI = true;
    await this.processUserExperiments();
    await this.getCart();
    await this.calculateCashBalance();
    this.store.select(fromSelectors.selectCartProducts).subscribe((products: any): any => this.productsInCart = { ...products });
    await this.loadData();
    this.eventLogger.trackWebPage();
    this.eventLogger.trackEvent('reorder_page', { username: this.user.get('username') });
    this.eventLogger.cleverTapEvent('pageOpen', JSON.stringify({ pageName: 'product-list' }));
    this.eventLogger.trackInElasticSearch({ username: this.user.get('username'),
      added: new Date(),
      type: 'webApp',
      message: 'User into reorder page',
      event: 'REORDER_PAGE' });
    this.loading = false;
  }

  async processUserExperiments(): Promise<void> {
    this.experiments = await this.conn.findUserActiveExperiments(this.user?.get('username'));
    this.experiments.forEach(async (experiment: any): Promise<any> => {
      if (experiment.key === 'buy1_get1_sale') {
        this.saleVariants = experiment.variant;
        this.isBOGOSaleLive = true;
      }
      if (experiment.key === 'perosonlized_bogo') {
        this.isPersonalizedBogoExperiment = true;
        this.personalizedBogoTitle = experiment.variant;
      }
      if (experiment.key === 'shop_search') {
        this.isSearchOn = true;
      }
      if (experiment.key === 'advanced_search') {
        this.isAdvancedSearchOn = true;
      }
      if (experiment.key === 'aov_gamification' && this.user?.get('orderState') === this.appConfig.Shared.User.OrderState.DELIVERED) {
        await this.setAOVValues(experiment);
        await this.getCart();
      }
      if (experiment.key === 'new_cart_redesign') {
        this.newCartUI = true;
      }
      if (experiment.key === 'shop_revamp') {
        this.isShopRevampExperimentOn = true;
      }
    });
  }

  async setAOVValues(experiment: any): Promise<void> {
    this.aovData = experiment.variant;
    this.maxPrice = this.aovData.totalValue;
    const product = await this.conn.findCatalogWithKey([this.aovData.productID]);
    [this.freeProduct] = JSON.parse(JSON.stringify(product));
    if (this.freeProduct?.rebrandedImageWithoutBackground?.length) {
      this.prodImage = this.freeProduct.rebrandedImageWithoutBackground[0];
    } else if (this.aovData?.productImage) {
      this.prodImage = this.aovData.productImage;
    } else if (this.freeProduct.rebrandedImageWithBackground?.length) {
      this.prodImage = this.freeProduct.rebrandedImageWithBackground[0];
    } else if (this.freeProduct?.productUnboxedImage?.length) {
      this.prodImage = this.freeProduct.productUnboxedImage[0];
    }
    this.aovTask = true;
  }

  async calculateCashBalance(): Promise<void> {
    this.cashBalance = await this.conn.fetchCashBalance();
    this.tempCashBalance = this.cashBalance;
    this.minimumCash = 0;

    if (this.cashBalance > 0) {
      this.showCashBalance = true;
    }
  }

  goToCart(): void {
    this.router.navigate(['cart']);
  }

  async getCart(): Promise<void> {
    this.cart = await this.conn.getCart();
    this.cartItems = this.cart?.get('lineItems');
    if (this.cartItems?.length) {
      this.toolbarIcons[0].cart = true;
      let totalItems: number = 0;
      this.cartItems.forEach((element: any): void => {
        totalItems += element.quantity;
      });
      this.toolbarIcons[0].cartValue = totalItems || null;

      // Calculate original SP first
      let originalSP = 0;
      this.cartItems.forEach((element: any): void => {
        originalSP += element.quantity * element.price;
      });
      originalSP = Math.floor(originalSP);

      // Ensure cash balance is initialized
      if (!this.tempCashBalance) {
        this.cashBalance = await this.conn.fetchCashBalance();
        this.tempCashBalance = this.cashBalance;
      }

      // Calculate final totalSP with cash balance
      let finalTotalSP = originalSP;

      // Then calculate all discounts
      this.calculateDiscount();

      // Handle free product logic based on originalSP
      if (this.aovTask) {
        await this.handleFreeProduct(originalSP, finalTotalSP);
      }
    } else {
      if (this.aovTask) {
        this.barStyle.width = '1%';
        this.barStyle.backgroundColor = '#D66736';
        this.amountRemaining = this.maxPrice;
        const found = this.cartItems.some((el: any): any => el.productId === this.aovData.productID);
        if (found) {
          const params1 = {
            productId: this.aovData.productID,
            quantity: 1,
          };
          await this.conn.removeProductFromCart(params1);
          await this.getCart();
        }
      }
      this.toolbarIcons[0].cartValue = null;
    }

    // Prevent multiple quantities of free product
    if (this.aovTask) {
      const product = this.cartItems.find((each: any): any => each.price === 0);
      if (product?.quantity > 1) {
        const params1 = {
          productId: this.aovData.productID,
          quantity: 1,
        };
        await this.conn.removeProductFromCart(params1);
        await this.getCart();
      }
    }
  }

  async handleFreeProduct(originalSP: number, finalTotalSP: number): Promise<void> {
    this.percentageDone = Math.round((finalTotalSP / this.maxPrice) * 100);
    const foundTwo = this.cartItems.some((el: any): any => el.productId === this.aovData.productID && el.quantity > 1,
    );

    // Check finalTotalSP (after cash balance) for free product eligibility
    if (finalTotalSP < this.maxPrice || foundTwo) {
      const found = this.cartItems.some((el: any): any => el.productId === this.aovData.productID,
      );
      if (found) {
        const params1 = {
          productId: this.aovData.productID,
          quantity: 1,
        };
        await this.conn.removeProductFromCart(params1);
        await this.getCart();
      }
      this.amountRemaining = this.maxPrice - finalTotalSP;
      this.barStyle.width = `${this.percentageDone}%`;
      this.barStyle.backgroundColor = '#D66736';
    } else {
      this.barStyle.width = '100%';
      this.barStyle.backgroundColor = '#5EAC73';
      this.amountRemaining = 0;

      const found = this.cartItems.some((el: any): any => el.productId === this.aovData.productID,
      );
      if (!found) {
        const params1 = {
          productId: this.aovData.productID,
          quantity: 1,
        };
        await this.conn.addProductToCart(params1);
        await this.getCart();
        this.showConfettiAnimation = true;
        setTimeout((): any => {
          this.showConfettiAnimation = false;
        }, 2000);
      }
    }
  }

  /**
   * Fetches are re-order'able products & addon products.
   * 1. If products id is mentioned in queryParam, then we by default add 1 quantity in cart for those products.
   * 2. If no products mentioned in params, then we show all products that are fetched.
   * 3. We sort it based on quantity, to bring product that are added in cart to focus.
   * 4. We calculate total MRP & SP for products in cart.
   * 5. We calculate discount applied on the total.
   */
  async loadData(): Promise<any> {
    const params = this.route.snapshot.queryParams;
    let tags: any[];
    let excludeTag: any[];
    let products: any = [];
    if (this.isSearchOn) {
      this.searchKey = params.searchKey;
      if (params.from === 'search' && params.searchKey) {
        this.eventLogger.trackInElasticSearch({
          event: 'PRODUCT_SEARCH',
          type: 'APP_SEARCH_LOG',
          key: this.searchKey,
          user: this.user.get('username'),
        });
        this.searchProducts = await this.conn.findProductsByText(params.searchKey);
        products = JSON.parse(JSON.stringify(this.searchProducts));
        this.eventLogger.cleverTapEvent('pageOpen', JSON.stringify({ name: 'search-page-open' }));
      }
    }

    if (params.from === 'saleProducts') {
      this.allSaleProducts = await this.conn.findSaleProds();
      this.allSaleProducts.sort((element1: any, element2: any): number => {
        if (element1.get('ranking') === element2.get('ranking')) {
          return 0;
        }
        return element1.get('ranking') > element2.get('ranking') ? 1 : -1;
      });
      products = this.allSaleProducts;
    }
    if (params.from === 'reorderProducts') {
      tags = ['RE_ORDER'];
      products = await this.conn.findSectionProductsServerAPI(
        this.appConfig.Shared.ShopSections.TYPE.ReOrder,
        true,
      );
    }
    if (params.from === 'recommendedProducts') {
      tags = ['ADD_ON', 'RE_ORDER'];
      products = await this.conn.findSectionProductsServerAPI(
        this.appConfig.Shared.ShopSections.TYPE.Recommended,
        true,
      );
    }
    if (params.from === 'bogoProduct' && this.isBOGOSaleLive) {
      products = await this.fetchBogoProducts();
    }
    if (params.from && params.type === shopSectionNames.collection) {
      const productCollection = await this.conn.fetchProductCollection(params.from);
      this.collectionTitle = productCollection?.title || 'Collection Products';
      products = productCollection?.products || [];
    }

    if (params.from !== 'search' && !this.refactoredSections.includes(params.from) && params.type !== shopSectionNames.collection) {
      if (this.isBOGOSaleLive) {
        // eslint-disable-next-line max-len
        if (!params.tags
          && params.from !== 'saleProducts'
          && params.from !== 'holiSale' && params.from !== 'kitsProducts' && params.from !== 'bogoProduct') {
          if (params.showAll && !tags) {
            tags = ['ADD_ON', 'RE_ORDER'];
          }
          products = await this.conn.findAllProductsForUser(tags, true);
        }
      } else if (!params.tags) {
        if (params.showAll && !tags) {
          tags = ['ADD_ON', 'RE_ORDER'];
          excludeTag = ['BOGO_PRODUCT'];
        }
        products = await this.conn.findAllProductsForUser(tags, true, excludeTag);
      }

      products = JSON.parse(JSON.stringify(products));

      if (params.from !== 'reorderProducts' && !params.showAll && params.from !== 'bogoProduct'
        && params.from !== 'drRecommendedProducts') {
        products.forEach((element: any, index: any): any => {
          const tagsList = element.tags;
          if (tagsList?.includes('RE_ORDER')) {
            if (!tagsList.includes('ADD_ON')) {
              products.splice(index, 1);
            }
          }
        });
      }
    }
    if (params?.from && params.type !== shopSectionNames.collection) {
      this.eventLogger.cleverTapEvent('pageOpen', JSON.stringify({ pageName: 'product-list' }));
      if (params.from === 'reorderProducts') this.toolbarTitle = 'Reorder Products';
      else if (params.from === 'recommendedProducts') this.toolbarTitle = 'Recommended Products';
      else if (params.from === 'bogoProduct') this.toolbarTitle = 'Sale Products';
    }
    const productsFromUrl: string[] = params.products ? params.products.split(',') : [];
    if (productsFromUrl.length) {
      let productsFromParams = await this.conn.findProductsById(productsFromUrl);
      productsFromParams = JSON.parse(JSON.stringify(productsFromParams));
      productsFromParams.forEach((each: any): void => {
        const isProductFoundFromUrl: boolean = !!products.find((product: any): boolean => product.objectId === each.objectId);
        if (!isProductFoundFromUrl) products.push(each);
        else this.productsInCart[each.objectId] = 1;
      });
    }
    let productsFromParams = await this.conn.findProductsById(productsFromUrl);
    productsFromParams = JSON.parse(JSON.stringify(productsFromParams));
    if (productsFromParams.length === 1) {
      const found = this.cartItems.some((el: any): any => el.productId === productsFromUrl[0]);
      if (!found) {
        const params1 = {
          productId: productsFromParams[0].objectId,
          quantity: 1,
        };
        await this.conn.addProductToCart(params1);
        this.getCart();
      }
    }
    if (this.cartItems.length) {
      this.cartItems.forEach((each: any): void => {
        let isProductFoundFromUrl: boolean;
        if (tags && !tags.includes('ADD_ON')) {
          isProductFoundFromUrl = !!products.find((product: any): boolean => (product.objectId === each.productId
            && product?.tags?.includes('RE_ORDER')));
          this.productsInCart[each.productId] = each.quantity;
        } else {
          this.productsInCart[each.productId] = each.quantity;
        }
      });
    }
    if (!this.cartItems.length && !productsFromParams.length) {
      this.productsInCart = {};
    }
    if (this.productsInCart && this.cartItems.length && !productsFromParams.length) {
      this.productsInCart = {};
      const result = this.cartItems.filter((o1: any): any => products.some((o2: any): any => o2.objectId === o1.productId));
      result.forEach((element: any): any => {
        this.productsInCart[element.productId] = element.quantity;
      });
    }
    products.sort((valueA: any, valueB: any): number => {
      if (this.productsInCart[valueA.objectId] && !this.productsInCart[valueB.objectId]) return -1;
      return 1;
    });
    this.store.dispatch(fromActions.CartUpdateProductsBegin({ products: this.productsInCart }));
    // Remove sale products from reorder products
    // products = JSON.parse(JSON.stringify(products));

    if (params.from === 'reorderProducts') {
      products = products.filter((prod: any): boolean => !(prod.isSaleProduct && prod.margUnit === 2));
    }
    if (params.from === 'recommendedProducts') {
      products = products.filter((prod: any): boolean => !(prod.isSaleProduct && prod.margUnit === 2));
    }
    if (this.isPersonalizedBogoExperiment && params.from === 'bogoProduct') {
      products = products.filter((prod: any): boolean => prod.type !== 'main');
    }
    if (params.from !== 'reorderProducts' && params.type !== 'collection') {
      products = products.filter((prod: any): boolean => prod.inventoryStatus !== this.appConfig.Shared.Inventory.Type.DISCONTINUED);
    }
    this.productsAvailable = products;

    this.cart.get('lineItems').forEach((product: any): void => {
      this.totalMRP += product.quantity * product.mrp;
    });
    this.totalMRP = Math.round(this.totalMRP);
    this.totalSP = Math.round(this.cart.get('totalPrice'));
    this.calculateDiscount();
  }

  async fetchBogoProducts(): Promise<any> {
    const saleProds = await this.conn.findBogoSaleProds();
    const availableSaleProducts = [];
    saleProds?.forEach((element: any): any => {
      availableSaleProducts.push(element.get('product'));
    });
    availableSaleProducts.filter((product:any):any => product.get('inventoryStatus') === 'AVAILABLE');
    availableSaleProducts?.sort((element1: any, element2: any): number => {
      if (element1.get('ranking') === element2.get('ranking')) {
        return 0;
      }
      return element1.get('ranking') > element2.get('ranking') ? 1 : -1;
    });
    return availableSaleProducts;
  }
  /**
   * We add or remove product in cart
   * Uses cart service to handle product eligibility, quantity limits, and cart updates.
   * For reorder products with alternate, calls replaceDiscontinuedProduct before adding to cart.
   * Recalculate total & discount after successful operation.
   */
  async addOrRemoveFromCart(index: number, action: string, event: any, img?: any): Promise<void> {
    event.stopPropagation();
    const product = this.productsAvailable[index];
    this.oldProduct = this.productsAvailable[index];

    // For ADD action, check if product is discontinued/restricted and get user confirmation
    let targetProduct = product;
    if (action === this.appConfig.Shared.Cart.Action.ADD) {
      const guardResult = await this.cartService.checkProductAllowedForCartAndPrompt(product, {
        productsInCart: this.productsInCart,
        section: this.getSectionFromRoute()
      });
      if (!guardResult?.allowed) {
        // User cancelled popup or product not allowed
        return;
      }

      // Use alternate product if provided by guard
      targetProduct = guardResult.overrideProduct || product;

      // If this is a reorder product and user accepted alternate, update server-side mapping
      const params = this.route.snapshot.queryParams;
      const isReorderSection = params.from === 'reorderProducts';
      if (guardResult.overrideProduct && isReorderSection) {
        try {
          // Set old and new product for cache update
          this.newProduct = targetProduct;

          await this.conn.replaceDiscontinuedProduct(
            product.objectId,
            targetProduct.id || targetProduct.objectId
          );
          // Update cache after replacing product
          this.updateCache();
        } catch (error) {
          // If replace fails, continue with add to cart
        }
      }
    }

    // Prepare options for cart service
    const options: CartUpdateOptions = {
      img: img,
      productsInCart: this.productsInCart,
      cartItems: this.cartItems,
      skipGuardCheck: action === this.appConfig.Shared.Cart.Action.ADD // Skip guard check since we already did it
    };

    // Use cart service to add or remove from cart with target product (alternate if exists)
    // Cart service handles: quantity limits and cart updates
    const result = await this.cartService.addOrRemoveFromCart(targetProduct, action, options);

    // If operation was successful, update local state and refresh cart
    if (result.success) {
      this.productsInCart = result.updatedProductsInCart;
      // Animation is handled by cart service when img is provided in options
      await this.getCart();

      // Log events in component (not in cart service)
      if (action === this.appConfig.Shared.Cart.Action.ADD && this.searchProducts.length > 0) {
        this.eventLogger.cleverTapEvent('click', JSON.stringify({
          name: 'search-add-to-cart',
          value: this.productsAvailable[index].id || this.productsAvailable[index].objectId
        }));
      }
    }

    // Update store and recalculate discount
    this.store.dispatch(fromActions.CartUpdateProductsBegin({ products: this.productsInCart }));
    this.calculateDiscount();
  }

  animateAddToCartProduct(product: any): void {
    this.animationProduct = product;
    setTimeout((): void => {
      this.animationProduct = null;
    }, 1000);
  }

  animateAddToCart(img?: HTMLImageElement): void {
    const image = new Image();
    image.src = img.src;
    image.style.position = 'absolute';
    image.style.top = `${img.getBoundingClientRect().top}px`;
    image.style.right = `${this.windowRef.nativeWindow.innerWidth - img.getBoundingClientRect().right}px`;
    image.style.height = `${img.offsetHeight}px`;
    image.style.width = 'auto';
    image.style.zIndex = '1000';

    image.classList.add('fly-animation');
    image.onanimationend = ((event: any): void => {
      image.remove();
    });
    this.windowRef.nativeWindow.document.body.appendChild(image);
  }

  async viewProduct(product: any): Promise<void> {
    // Guard against discontinued/restricted products; shows popup if needed
    const guardResult = await this.cartService.checkProductAllowedForCartAndPrompt(product, {
      productsInCart: this.productsInCart,
      section: this.getSectionFromRoute(),
      navigateToArticlePage: true,
    });

    if (!guardResult?.allowed) {
      // User cancelled popup or product not allowed
      // If user clicked "Read More" in popup, handleDiscontinuedReadMore will navigate
      // If user cancelled, do nothing
      return;
    }

    // If alternate product was provided and user confirmed, add to cart instead of navigating
    // This handles the case when user clicks "Add to Cart" in the discontinued popup
    if (guardResult.overrideProduct) {
      const targetProduct = guardResult.overrideProduct;

      // If this is a reorder product, update server-side mapping
      const params = this.route.snapshot.queryParams;
      const isReorderSection = params.from === 'reorderProducts';
      if (isReorderSection) {
        try {
          this.oldProduct = product;
          this.newProduct = targetProduct;

          await this.conn.replaceDiscontinuedProduct(
            product.objectId,
            targetProduct.id || targetProduct.objectId
          );
          this.updateCache();
        } catch (error) {
          // If replace fails, continue with add to cart
        }
      }

      // Add alternate product to cart
      const options: CartUpdateOptions = {
        img: undefined,
        productsInCart: this.productsInCart,
        cartItems: this.cartItems,
        skipGuardCheck: true // Already checked above
      };

      const result = await this.cartService.addOrRemoveFromCart(targetProduct, this.appConfig.Shared.Cart.Action.ADD, options);

      if (result.success) {
        this.productsInCart = result.updatedProductsInCart;
        await this.getCart();
        this.store.dispatch(fromActions.CartUpdateProductsBegin({ products: this.productsInCart }));
        this.calculateDiscount();
      }

      // Don't navigate when adding to cart from viewProduct
      return;
    }

    // No alternate product or user just wants to view - navigate to product detail page
    this.localStorage.setValue('scrollID', product.objectId);
    const productData = JSON.parse(JSON.stringify(product));
    if (this.searchProducts.length > 0) {
      this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'search-product', value: productData.objectId }));
      await this.router.navigate([`/product/${productData.objectId}`],
        { queryParams: { section: 'searchProduct' } });
    } else {
      await this.conn.navigateToURL(`/product/${productData.objectId}`);
    }
  }

  calculateDiscount(): void {
    let originalSP = 0;
    this.totalMRP = 0;

    // Calculate original totals
    this.cartItems.forEach((element: any): any => {
      originalSP += element.quantity * element.price;
      this.totalMRP += element.quantity * element.mrp;
    });

    // Round the totals
    this.totalMRP = Math.floor(this.totalMRP);
    originalSP = Math.floor(originalSP);

    // Set initial totalSP
    this.totalSP = originalSP;

    // Apply cash balance if available - simplified condition
    if (this.tempCashBalance > 0 && !(this.experimentService.applyCashExperiment && this.user?.get('orderState') === this.appConfig.Shared.User.OrderState.DELIVERED)) {
      const cashDiscount = Math.min(this.tempCashBalance, originalSP);
      this.totalSP = originalSP - cashDiscount;
      this.cashBalance = cashDiscount;
    }

    // Calculate final discount percentage
    let discount = 0;
    if (this.totalMRP > this.totalSP) {
      discount = Math.floor(((this.totalMRP - this.totalSP) * 100) / this.totalMRP);
    }
    this.discount = discount;

    // Use originalSP for AOV check
    if (this.aovTask) {
      this.percentageDone = Math.round((this.totalSP / this.maxPrice) * 100);
    }
  }

  showError(message: any): void {
    this.broadcast.broadcast('NOTIFY', { message });
  }

  // TODO : support sending quantities selected to api and create order for specified quantity.
  /**
   * Send all selected products id in queryParam to checkout page.
   * Note: We are now not sending quantity to backend. We have to support this feat soon. Right now its all considered as 1 quantity.
   */
  checkout(): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'product-list-buy-now' }));
    this.eventLogger.trackEvent('reorder_buy_now', { username: this.user.get('username') });
    this.eventLogger.trackInElasticSearch({ username: this.user.get('username'),
      added: new Date(),
      type: 'webApp',
      message: 'User clicked reorder buy ',
      event: 'REORDER_BUY' });

    this.router.navigate(['cart']);
  }

  navigateTo(url: string, params?: Record<string, any>): void {
    this.router.navigate([url], params);
  }

  back(): void {
    this.router.navigate(['/user'], { queryParams: { tab: 'shop', back: 'home' } });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription): void => subscription.unsubscribe());
  }

  closeDialog(): void {
    this.selfReorderWarning = false;
  }

  closePopup(isReplace: boolean = false): void {
    this.isDiscontinued = false;
    this.broadcast.broadcast('NAVIGATION_BACK');
    if (isReplace) {
      this.isReplaced = true;
    }
  }

  updateCache(): void {
    const existingReorderCache = this.cacheService.retrieve('CureSkin/reorderProducts');
    const existingShopCache = this.cacheService.retrieve('CureSkin/shopSection');
    if (existingReorderCache) {
      const cachedReorder = existingReorderCache.data.result;
      for (let i = 0; i < cachedReorder.length; i += 1) {
        const currentReorderCache = cachedReorder[i];
        if (currentReorderCache.objectId === this.oldProduct.objectId) {
          cachedReorder[i] = this.newProduct;
          break;
        }
      }
      const updatedReorderCache = JSON.parse(JSON.stringify(existingReorderCache));
      updatedReorderCache.data.result = cachedReorder;
      this.cacheService.store('CureSkin/reorderProducts', updatedReorderCache);
    }

    if (existingShopCache) {
      const cachedShop = existingShopCache.data.result.ReOrder;
      for (let i = 0; i < cachedShop.length; i += 1) {
        const currentShopCache = cachedShop[i];
        if (currentShopCache.objectId === this.oldProduct.objectId) {
          cachedShop[i] = this.newProduct;
          break;
        }
      }
      const updatedShopCache = JSON.parse(JSON.stringify(existingShopCache));
      updatedShopCache.data.result.ReOrder = cachedShop;
      this.cacheService.store('CureSkin/shopSection', updatedShopCache);
    }
  }

  async changeProduct(): Promise<void> {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'discontinued-product-changed' }));
    this.loading = true;
    const res = await this.conn.replaceDiscontinuedProduct(this.oldProduct.objectId, this.newProduct.objectId);
    this.updateCache();
    this.isDiscontinued = false;
    if (res) {
      const params = {
        productId: this.newProduct.objectId,
        quantity: 1,
      };
      const cart = await this.conn.addProductToCart(params);
      if (cart) {
        await this.getCart();
        this.isDiscontinued = false;
        this.isReplaced = true;
        await this.loadData();
      }
    }
    this.loading = false;
  }

  async openArticle(): Promise<any> {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'discontinued-product-changed' }));
    await this.conn.replaceDiscontinuedProduct(this.oldProduct.objectId, this.newProduct.objectId);
    this.updateCache();
    this.isDiscontinued = false;
    this.isReplaced = true;
    const params = this.route.snapshot.queryParams;
    let query: any = '';
    if (params.from) {
      query = `from=${params.from}`;
    } else {
      query = 'showAll=true';
    }
    this.location.replaceState('user/order/reorder', query);
    this.viewProduct(this.newProduct);
  }
  checkoutAOV(): void {
    if (this.toolbarIcons[0].cartValue) {
      this.router.navigate(['cart']);
    }
  }

  private getSectionFromRoute(): string {
    const from = this.route.snapshot.queryParams.from;
    if (!from) return '';
    const sections = this.appConfig.Shared.Shop?.Sections;
    if (from === this.appConfig.Shared.ViewAllPage.queryParams.BOGO) return sections.BOGO;
    if (from === this.appConfig.Shared.ViewAllPage.queryParams.PERSONALIZED_BOGO) return sections.PERSONALIZED_BOGO;
    if (from === this.appConfig.Shared.ViewAllPage.queryParams.REORDER) return sections.REORDER;
    return '';
  }

}
